import { animate, style, transition, trigger } from "@angular/animations";
import {
	ChangeDetectorRef,
	Component,
	ElementRef,
	Inject,
	NgZone,
	OnDestroy,
	OnInit,
	Optional,
	Renderer2,
} from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { CsToastManagerService } from "@cs/components/toast-manager";
import { CsHttpRequestOptions, DataDescribed, LoggerUtil } from "@cs/core";
import { AuthenticationQuery } from "@cs/performance-manager/shared";
import { TranslateService } from "@ngx-translate/core";
import { DefaultComponent } from "../default/default.component";
import {
	DataDescribedLoginService,
	LoginConfigService,
	SHOW_REQUEST_ACCESS,
} from "../login-config.service";
import { LoginModel } from "../models/accountInfo";
import { AuthMethod } from "../models/auth-method.model";
import { LoginQuery } from "../state/login.query";
import { LoginService } from "../state/login.service";
import { Subscription } from "rxjs";
import { retry, timeout } from "rxjs/operators";

declare var grecaptcha: any;

@Component({
	standalone: false,
	selector: "pmc-data-described-login",
	templateUrl: "./data-described-login.component.html",
	styles: [
		`
			.login-form-loading,
			.login-form-error {
				min-height: 188px;
				padding: 24px 0;
				gap: 16px;
				text-align: center;
			}

			.login-form-error__icon {
				font-size: 40px;
				line-height: 1;
				color: var(--warning-color, #e0a800);
			}

			.login-form-error__message {
				margin: 0;
				color: var(--secondary-text-color, #6b7684);
			}
		`,
	],
	animations: [
		trigger("inOutAnimation", [
			transition(":enter", [
				style({ transform: "translateX(-100%)", height: 48, marginTop: 0 }),
				animate(
					"500ms cubic-bezier(0.4, 0.0, 0.2, 1)",
					style({ transform: "translateX(0)", height: 48 }),
				),
			]),
			transition(":leave", [
				style({ height: 0 }),
				animate(
					"500ms cubic-bezier(0.4, 0.0, 0.2, 1)",
					style({ height: 0, opacity: 0, marginTop: -8 }),
				),
			]),
		]),
	],
})
export class DataDescribedLoginComponent
	extends DefaultComponent
	implements OnInit, OnDestroy
{
	loginConfig: DataDescribedLoginService;
	formData: DataDescribed<LoginModel[]>;
	get showRequestAccess(): boolean {
		if (!this.showRequestAccessToken) return false;
		// Show the link unless the server explicitly returns false for this method.
		// undefined / null = not specified by server → assume available (default open).
		return this.loginModel?.requestNewAccountAvailable !== false;
	}

	/**
	 * Whether the username field should be shown.
	 * Defaults to `true` when the server does not return `requiresLoginname`
	 * (undefined / null → field is shown). Only hides when explicitly `false`.
	 */
	get showUsernameField(): boolean {
		return this.loginModel?.requiresLoginname !== false;
	}

	loginOptions: Array<AuthMethod>;

	loginModel: LoginModel = {
		googleCaptchaResponse: "",
		username: "",
		password: "",
	};

	/** True when the server returned 309 with no redirect URL (LDAP/SAML user without account) */
	showLdapPanel = false;

	/** LDAP username captured at 309 response — forwarded as router state to the wizard */
	ldapUsername = "";

	/**
	 * Binding to the captcha field
	 */
	captcha = "";
	/**
	 * The state of the form. Defaults to true because it's empty
	 */
	isErrorState = true;

	/**
	 * HTML of the Captcha
	 */
	captchaHtml: SafeHtml;

	/**
	 * Id of the reCAPTCHA widget rendered by refreshCaptcha() (render=explicit —
	 * we own the only render). Used to `grecaptcha.execute()` the challenge from
	 * submitWithCaptcha() and to `grecaptcha.reset()` the consumed token after a
	 * failed login (COH-3297). Null until the first render.
	 */
	private captchaWidgetId: number | null = null;

	/**
	 * Indicator for the loader
	 */
	isLoadingCaptcha = true;

	/**
	 * True while the server-described login form is being fetched. Until it
	 * resolves we only have the empty default model, which would render as a
	 * misleading single "username" line — so we show a loader instead (COH-3297).
	 */
	isLoadingForm = true;

	/**
	 * True when the login form could not be loaded after retries. Drives the
	 * inline error + manual-retry UI instead of leaving a form that looks hung.
	 */
	formLoadFailed = false;

	/**
	 * Abort a form fetch that never returns after this many ms so it falls into
	 * the retry/error path rather than spinning forever (the COH-3297 "hang").
	 */
	private readonly FORM_LOAD_TIMEOUT_MS = 20_000;

	/** Active form-fetch subscription, so a retry can cancel/replace the previous. */
	private formLoadSub?: Subscription;

	useGoogleCaptcha = false;

	//#region Loaders

	/**
	 * Loader flag for the reset password button
	 */
	isWaitingForReset = false;

	get activeMethod(): string {
		return this._activeMethod;
	}

	set activeMethod(value: string) {
		this._activeMethod = value;
	}

	get currentAuthMethod() {
		return this.loginOptions.find((value) => value.active);
	}

	constructor(
		readonly loginService: LoginService,
		readonly loginQuery: LoginQuery,
		@Inject(LoginConfigService) loginConfig: DataDescribedLoginService,
		readonly route: ActivatedRoute,
		readonly authenticationQuery: AuthenticationQuery,
		readonly toastService: CsToastManagerService,
		readonly l8n: TranslateService,
		readonly renderer: Renderer2,
		readonly sanitizer: DomSanitizer,
		readonly changeRef: ChangeDetectorRef,
		readonly ngZone: NgZone,
		private element: ElementRef,
		@Optional() @Inject(SHOW_REQUEST_ACCESS) private showRequestAccessToken: boolean | null,
		private readonly router: Router,
	) {
		super(
			loginService,
			loginQuery,
			loginConfig,
			authenticationQuery,
			route,
			toastService,
			l8n,
		);


		window["onloadRecaptchaCallback"] = () => {
			this.refreshCaptcha();
		};

		// Receives the token once the (invisible) challenge completes. The form is
		// already validated in submitWithCaptcha() before the challenge runs.
		window["submitForm"] = (token) =>
			this.ngZone.run(() => {
				this.captcha = token;
				this.loginModel.googleCaptchaResponse = token;
				this.login();
			});
	}

	ngOnInit() {
		super.ngOnInit();
		this.loadLoginForm();
	}

	/**
	 * Fetch the server-described login form and apply it. The form dictates which
	 * fields (username / password / captcha) and auth providers to render, so
	 * before it arrives the component only has the empty default model — which
	 * paints as a single, misleading "username" line. We therefore keep a loader
	 * up until it resolves, time out a request that never returns, retry transient
	 * failures (e.g. a slow captcha/network on the login page), and surface an
	 * inline retry if it ultimately fails, rather than leaving a hung-looking form
	 * (COH-3297).
	 */
	private loadLoginForm(): void {
		this.isLoadingForm = true;
		this.formLoadFailed = false;
		this.changeRef.detectChanges();

		this.formLoadSub?.unsubscribe();
		this.formLoadSub = this.loginConfig
			.getLoginForm()
			.pipe(
				// A request that hangs (the COH-3297 symptom) becomes a retryable
				// error instead of an endless spinner.
				timeout(this.FORM_LOAD_TIMEOUT_MS),
				// Self-heal transient network/captcha-load blips before giving up.
				retry({ count: 2, delay: 1500 }),
			)
			.subscribe({
				next: (value) => {
					this.applyLoginForm(value.value);
					this.isLoadingForm = false;
					this.changeRef.detectChanges();
				},
				error: (error) => {
					LoggerUtil.log(`Failed to load the login form: ${error?.message ?? error}`);
					this.isLoadingForm = false;
					this.formLoadFailed = true;
					this.changeRef.detectChanges();
					this.toastService.show({
						type        : "error",
						content     : this.l8n.instant("MSG_LOGIN_FORM_LOAD_FAILED"),
						clickToClose: true,
					});
				},
			});
	}

	/** Manual retry from the inline error state (button in the template). */
	retryLoadLoginForm(): void {
		this.loadLoginForm();
	}

	/** Apply a freshly-loaded server form description to the component state. */
	private applyLoginForm(formData: DataDescribed<LoginModel[]>): void {
		this.formData = formData;

		this.formData.layout?.alerts?.forEach(alert => {
			this.toastService.show({
				type           : 'warning',
				title          : alert.title,
				content        : alert.message,
				clickToClose   : true,
				showProgressBar: false,
			});
		});

		this.availableAuthProviders = JSON.parse(JSON.stringify(this.formData.data));

		const loginState = this.loginQuery.getValue();

		// Honour the auth method the server flagged as active (it remembers
		// the user's last-used method, e.g. HQ). Fall back to the method
		// stored in app state, then to the hardcoded default. Without this
		// the form always opens on UsernamePassword and the server's chosen
		// SSO provider (and its onLoginClickRedirectToUrl) is ignored.
		const serverActiveMethod = this.availableAuthProviders.find(
			(provider) => provider.active,
		)?.method;

		this.activeMethod =
			serverActiveMethod ??
			(loginState.method == null ? this.activeMethod : loginState.method);

		this.loginOptions = this.formData // @ts-ignore
			.getLookupByProperty("method")
			.lookup.values.map(
				(value1) =>
					new AuthMethod({
						label: value1.value,
						key: value1.key,
						icon: this.getAuthIcon(value1.key),
						active: this.activeMethod === value1.key,
					}),
			);

		// Save the login options for extenal login page label resolvement
		this.loginService.setLoginOptions(
			JSON.parse(JSON.stringify(this.loginOptions)),
		);

		this.switchMethod(
			this.loginOptions.find((value1) => value1.active) || this.loginOptions[0],
		);
	}

	login() {
		if (this.loginQuery.getValue().inProgress) {
			LoggerUtil.log("Already in progress");
			return;
		}

		this.loginService.setErrorState(false);
		this.loginService.setInProgress(true);

		const errorHandler = new CsHttpRequestOptions();
		errorHandler.errorResponseHandler = (error) => {
			this.loginService.setInProgress(false);

			// Rebuild (NOT grecaptcha.reset) the captcha if the error is not a
			// redirect to otp (status 450): resetting an invisible widget rejects
			// its internal promises ("Uncaught (in promise) reCAPTCHA Timeout") and
			// can leave it so the next execute() silently never fires the callback —
			// a dead Login button on the retry (COH-3297).
			if (error.status !== 450) {
				this.rebuildCaptcha();
			}

			switch (error.status) {
				case 503:
					this.toastService.show({
						type: "error",
						content: this.l8n.instant("AUTH_PROVIDER_NOT_AVAILABLE_MSG", {
							methodName: this.currentAuthMethod.label,
						}),
					});
					return true;
				case 309:
					if (error.error) {
						window.location = error.error;
						return true;
					} else {
						// LDAP/SAML user exists but has no Cohelion account →
						// show the inline recognition panel instead of navigating away.
						this.ldapUsername = this.loginModel.username;
						this.showLdapPanel = true;
						this.loginService.setHideHeader(true);
					}
					return true;
				case 401:
					this.loginService.setErrorState(true);

					this.toastService.show({
						type: "info",
						showProgressBar: true,
						showTitle: false,
						content: `${this.l8n.instant("INVALID_LOGIN_MSG")}`,
					});

					return true;
			}
			return false;
		};

		// SSO providers (HQ, SAML, …) carry a redirect URL. Navigate straight to
		// it and hard-stop: otherwise we fall through to the loginWithProvider()
		// POST, which the server answers with a 309 whose handler hijacks the
		// navigation to the SAML response URL instead of the provider's own URL.
		if (this.loginModel?.onLoginClickRedirectToUrl) {
			window.location.href = this.loginModel.onLoginClickRedirectToUrl;
			return;
		}

		this.loginConfig
			.loginWithProvider(this.loginModel, errorHandler)
			.subscribe((value) => {
				this.loginConfig.loginSuccessHandler(value, this.route);
				// stop animation
				this.loginService.setInProgress(false);
			});

		this.rememberMe
			? this.loginService.setUserName(this.loginModel.username)
			: this.loginService.setUserName("");

		this.loginService.SetAuthMethod(this.loginModel.method);
		this.loginService.setRememberMe(this.rememberMe);
	}

	/** Navigate to the wizard (step-two / LDAP flow), skipping email verification. */
	requestAccount(): void {
		// Route /sso is the tokenless SSO wizard (requireToken: false, ssoFlow: true).
		// The LDAP username is forwarded as router state under the canonical key
		// used by RequestAccessStepTwoComponent (ssoUsername).
		this.router.navigate(["/login/request-access/sso"], {
			state: { ssoUsername: this.ldapUsername },
		});
	}

	/** Dismiss the LDAP recognition panel and return to the normal login form. */
	cancelLdapRequest(): void {
		this.showLdapPanel = false;
		this.loginService.setHideHeader(false);
		this.setAuthMethodMessage();
	}

	ngOnDestroy(): void {
		super.ngOnDestroy();
		this.formLoadSub?.unsubscribe();
		this.captchaHtml = "";
		// refreshCaptcha() re-parents the badge to document.body, so Angular
		// won't clean it up with the component — remove it explicitly.
		document.querySelector(".grecaptcha-badge")?.remove();
		this.changeRef.detectChanges();
	}

	getAuthIcon(option: string) {
		const method = option.toLowerCase();

		if (method.startsWith("ldap")) return "icon icon-microsoft-account";
		if (method === "usernamepassword") return "icon-cohelion";
		if (method === "duo") return "icon icon-duo-account";

		return "icon icon-cohelion";
	}

	switchMethod(authMethod: AuthMethod) {
		this.activeMethod = authMethod.key;
		for (const method of this.loginOptions) {
			method.active = this.activeMethod === method.key;
		}
		this.loginModel = this.availableAuthProviders.find(
			(value) => value.method === this.activeMethod,
		);

		// Pre-fill the username field with the server-provided `value` (the
		// remembered login name) when this method asks for a login name and the
		// field hasn't already been entered.
		if (
			this.loginModel?.requiresLoginname !== false &&
			this.loginModel?.value &&
			!this.loginModel.username
		) {
			this.loginModel.username = this.loginModel.value;
		}

		this.setAuthMethodMessage();

		this.useGoogleCaptcha = this.loginModel.requiresGoogleCaptcha;
		if (this.useGoogleCaptcha) {
			const injectedRootUrlHtml = this.loginModel.googleCaptchaChallenge;
			// This will return something like <div class="g-recaptcha" data-sitekey="#application.Settings.Google.Captcha.SiteKey#" data-callback="submitForm" data-bind="form-submit"></div>
			this.captchaHtml =
				this.sanitizer.bypassSecurityTrustHtml(injectedRootUrlHtml);
			this.injectDependency();
		}
	}

	filterActive(loginOptions: Array<AuthMethod>) {
		if (loginOptions == null) return [];
		return loginOptions.filter((value) => !value.active);
	}

	/**
	 * Tear down the current reCAPTCHA widget and build a fresh one. Used after a
	 * failed login attempt instead of grecaptcha.reset(): resetting an invisible
	 * widget rejects its internal promises and can leave it in a state where the
	 * next execute() silently never runs the challenge (COH-3297). Recreating the
	 * container through the [innerHtml] binding yields a virgin element, so
	 * refreshCaptcha() renders a brand-new widget with a fresh id.
	 */
	private rebuildCaptcha() {
		if (!this.loginModel.requiresGoogleCaptcha) return;

		const captchaMarkup = this.captchaHtml;
		this.captchaHtml    = "";
		this.changeRef.detectChanges();
		// The old widget's badge lives on document.body (re-parented) — without the
		// container teardown removing it, it must go explicitly or badges pile up.
		document.querySelector(".grecaptcha-badge")?.remove();
		this.captchaWidgetId = null;
		this.captchaHtml     = captchaMarkup;
		this.changeRef.detectChanges();
		this.refreshCaptcha();
	}

	refreshCaptcha() {
		if (!this.loginModel.requiresGoogleCaptcha) return;

		this.isLoadingCaptcha = false;
		this.changeRef.detectChanges();
		setTimeout(() => {
			const container = this.element.nativeElement.querySelector(".g-recaptcha");
			if (container) {
				try {
					// Rendered-ness is stamped on the container ELEMENT, not inferred
					// from child count: for invisible captchas the badge is the widget's
					// only child and it gets re-parented to document.body below, after
					// which a wrong-password refresh would see an "empty" container and
					// render the same element a second time — corrupting grecaptcha's
					// internal DOM refs ("Cannot read properties of null (reading
					// 'style')") and killing the widget (COH-3297). The stamp survives
					// the badge move and disappears together with the element when the
					// [innerHtml] binding recreates it — exactly the lifetime we need.
					if (container.getAttribute("data-cs-rendered") !== "true") {
						// Fresh (or recreated) container: render our own widget. The
						// script loads with render=explicit, so this is the only render.
						// A badge left behind by a previous widget instance is orphaned —
						// remove it before rendering anew.
						if (this.captchaWidgetId !== null) {
							document.querySelector(".grecaptcha-badge")?.remove();
						}
						// data-bind is stripped so grecaptcha does NOT also bind the
						// submit button — a bound widget goes dead after grecaptcha.reset()
						// (the "captcha broken after wrong password" symptom, COH-3297);
						// submitWithCaptcha() drives execute() explicitly instead, which
						// is the reset-safe invocation model.
						container.removeAttribute("data-bind");
						this.captchaWidgetId = grecaptcha.render(container, {
							sitekey: container.getAttribute("data-sitekey"),
							// The server div declares no data-size; explicit render then
							// defaults to the visible checkbox, so pin the invisible mode
							// this flow is designed for (execute() from submitWithCaptcha).
							size:    container.getAttribute("data-size") ?? "invisible",
							callback: (output) => {
								window["submitForm"](output);
							},
						});
						container.setAttribute("data-cs-rendered", "true");
					} else if (this.captchaWidgetId !== null) {
						// Live widget (e.g. after a failed login): clear the consumed/
						// expired token so the next submit runs a fresh challenge.
						grecaptcha.reset(this.captchaWidgetId);
					}
				} catch (e) {
					LoggerUtil.log(`Failed to render/reset the captcha: ${e?.message ?? e}`);
				}
			}

			// reCAPTCHA appends the badge inside the widget container, which is
			// nested inside elements with CSS stacking contexts (animations/transforms).
			// Moving it to document.body lets position:fixed work relative to the viewport.
			const badge = document.querySelector('.grecaptcha-badge');
			if (badge && badge.parentElement !== document.body) {
				document.body.appendChild(badge);
			}

			this.changeRef.detectChanges();
		}, 0);
		this.changeRef.detectChanges();
	}

	/**
	 * Submit entry point for the captcha login flow (button click / enter key):
	 * validate the form first, then programmatically run the invisible challenge.
	 * The token arrives in window.submitForm, which performs the actual login.
	 */
	submitWithCaptcha() {
		const form = document.getElementById("form") as HTMLFormElement;
		if (form && !form.checkValidity()) {
			form.reportValidity();
			return;
		}

		if (this.captchaWidgetId !== null) {
			try {
				grecaptcha.execute(this.captchaWidgetId);
			} catch (e) {
				LoggerUtil.log(`Failed to execute the captcha: ${e?.message ?? e}`);
			}
		} else {
			// Widget never rendered (script/network hiccup) — try to recover.
			this.refreshCaptcha();
		}
	}

	private availableAuthProviders: LoginModel[];

	private get alreadyInjectedDependency() {
		return document.getElementById("googleCaptcha") != null;
	}

	//#endregion

	private _activeMethod = "UsernamePassword";

	private setAuthMethodMessage() {
		this.loginService.setUnderTitleMessage(
			`<i class="mr-2 ${this.getAuthIcon(this.activeMethod)}"></i> ${this.l8n.instant("MSG_LOGIN_METHOD", { method: this.currentAuthMethod.label })}`,
		);
	}

	private injectDependency() {
		if (this.alreadyInjectedDependency) {
			this.refreshCaptcha();
			return;
		}

		const script = document.createElement("script");
		script.id = "googleCaptcha";
		// render=explicit: WE render the widget (refreshCaptcha) — without it the
		// script auto-renders the .g-recaptcha div on load, our render() then throws
		// "already rendered", and the auto-bound widget breaks after a reset().
		script.src =
			"https://www.google.com/recaptcha/api.js?onload=onloadRecaptchaCallback&render=explicit";
		script.type = "text/javascript";
		script.async = true;
		script.defer = true;
		script.charset = "utf-8";
		this.renderer.appendChild(document.head, script);
	}
}
